import { Box, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { ContractDto } from '../../../services/dtos/contract.dto';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import CustomTable from '../../commun/table';
import { RelevesDto } from '../../../services/dtos/releves.dto';
import { RowDto } from '../../../services/dtos/row.dto';
import { ConsommationsDto } from '../../../services/dtos/consommations.dto';
import { BarChart } from '@mui/x-charts/BarChart';
import './consommation.scss';
import { getConfigConsommations, getConsommations } from '../../../services/consommations.service';
import { getReleves } from '../../../services/releves.service';
import { showAxiosMessageError } from '../../../services/error-message.service';
import { useSnackbars } from '../../../hooks/snackbar.hook';
import { env } from '../../../env';
import { ConfigConsommationsDto } from '../../../services/dtos/config-consommations.dto';
import ConsommationTelereleve from './consommation-telereleve';

/**
 * Composant pour les tableau et graphiques des données de consommation.
 * @param props
 * @constructor
 */
export default function Consommation(props: { contract?: ContractDto }) {
    // Déclaration du nombre 0 nécessaire face au code smell
    const zero: number = 0;
    // Déclaration du nombre 6 : taille md
    const mdGrandEcran: number = 6;
    // Déclaration du nombre 12 : taille xs
    const mdPetitEcran: number = 12;
    // Déclaration des lignes du tableau à vide
    const [rows, setRows] = useState<Array<any>>([]);
    // Déclaration des années à afficher sur l'axe x du tableau des consommations
    const [annees, setAnnees] = useState<string[]>(['1990']);
    // Déclaration des quantités qui sera affichés dans le tableau des consommations
    const [quantites, setQuantites] = useState<number[]>([zero]);
    // Config compteur actif
    const [configWaterMeter, setConfigWaterMeter] = useState<ConfigConsommationsDto>();
    // Snackbar des messages
    const showMessage = useSnackbars();

    //Déclarations des variables
    const [loading, setLoading] = useState<boolean>(true);

    const [releves, setReleves] = useState<RelevesDto>();

    const [isSearching, setIsSearching] = useState<boolean>(false);

    const rowsPerPage = Number(env.REACT_APP_NUMBER_ELEMENT_TABLE_SMALL);
    // Page affichée
    const [page, setPage] = useState(0);

    //Permet de savoir si on est passé une fois dans l'useEffect
    const dataFetchedRef = useRef(false);

    // En-têtes des cellules du tableau
    const headCells = [
        {
            id: 'releve.dateReleve',
            label: 'Date de relevé',
            sort: true,
        },
        {
            id: 'releve.typeReleve',
            label: 'Type de relevé',
            sort: false,
        },
        {
            id: 'releve.indexReleve',
            label: 'Index',
            sort: false,
        },
    ];

    useEffect(() => {
        if (configWaterMeter !== undefined && !configWaterMeter?.compteurTelereleve) {
            //On récupère pour le graphe les donénes de consommation annuelle
            getConsommations(props.contract?.['@id'], '')
                .then(data => {
                    graphConsommationsUpdate(data);
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération des consommations annuelles.');
                    showAxiosMessageError(error, showMessage);
                });

            //On récupère pour le tableau les relevés
            getReleves(props.contract?.['@id'], page + 1, rowsPerPage, '&order[dateReleve]=desc')
                .then(data => {
                    setReleves(data);
                    tableRelevesUpdate(data);
                })
                .catch(error => {
                    console.error('Erreur lors de la récupération des relevés.');
                    showAxiosMessageError(error, showMessage);
                })
                .finally(() => {
                    setLoading(false);
                    setIsSearching(false);
                });
        } else if (configWaterMeter?.compteurTelereleve) {
            // on le fait uniquement si on a les infos compteur
            setLoading(false);
            setIsSearching(false);
        }
        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configWaterMeter]);

    // Récupération des consommations et releves à l'ouverture de la page.
    useEffect(() => {
        // Pour éviter de passer deux fois dans la requete
        if (dataFetchedRef.current) {
            return;
        }
        dataFetchedRef.current = true;
        setIsSearching(true);
        getConfigConsommations(props.contract?.['@id'])
            .then(data => {
                setConfigWaterMeter(data);
            })
            .catch(error => {
                setIsSearching(false);
                showAxiosMessageError(error, showMessage);
            });
        //https://stackoverflow.com/questions/55840294/how-to-fix-missing-dependency-warning-when-using-useeffect-react-hook
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Gestion du changement de page, avec la récupération d'une autre page de factures
     * @param event Événement
     * @param newPage Page à afficher
     */
    const handleOnPageChange = async (event: any, newPage: number) => {
        event.preventDefault();
        setIsSearching(true);
        //On récupère pour le tableau les relevés
        getReleves(props.contract?.['@id'], newPage + 1, rowsPerPage, '&order[dateReleve]=desc')
            .then(data => {
                setReleves(data);
                setPage(newPage);
                tableRelevesUpdate(data);
            })
            .catch(error => {
                console.error('Erreur lors de la récupération des relevés.');
                showAxiosMessageError(error, showMessage);
            })
            .finally(() => {
                setLoading(false);
                setIsSearching(false);
            });
    };

    /**
     * Gestion de la mise à jour des données à renvoyer au tables des releves
     * @param relevesFetched
     */
    const tableRelevesUpdate = (relevesFetched: RelevesDto) => {
        // On vérifie si on a des relevés
        // Pour utiliser le composant table, on doit renseigner d'abord le contenu des rows et des cells, tableau simple à deux dimensions
        if (Array.isArray(relevesFetched?.releves)) {
            //Lignes initialisés à vide
            let rowsInit: RowDto[] = [];
            relevesFetched?.releves.forEach((releve, index) => {
                // On compete ici les lignes du tableau
                let row: RowDto = {
                    key: index,
                    value: [
                        { key: 'dateReleve', value: new Date(releve.dateReleve).toLocaleDateString() },
                        { key: 'typeReleve', value: releve.typeReleve },
                        { key: 'indexReleve', value: releve.indexReleve },
                    ],
                };
                rowsInit.push(row);
            });
            setRows(rowsInit);
        }
    };

    /**
     * Gestion de la mise à jour des données à renvoyer au graphique des consommations
     * @param consommations
     */
    const graphConsommationsUpdate = (consommations: ConsommationsDto) => {
        // On vérifie si on a des relevés
        // Pour utiliser le composant table, on doit renseigner d'abord le contenu des rows et des cells, tableau simple à deux dimensions
        //On vérifie si on a des données de consommations
        if (Array.isArray(consommations?.consommations)) {
            //Déclaration des données à vide
            let tabAnnees: string[] = [];
            let tabQuantites: number[] = [];
            //Parcours les éléments
            consommations?.consommations.forEach(consommation => {
                //Ajout aux listes
                tabAnnees.push(consommation.annee);
                tabQuantites.push(Number(consommation.quantite));
            });
            setAnnees(tabAnnees);
            setQuantites(tabQuantites);
        }
    };

    //Taille de l'écran qui va faire varier l'affichage des éléments dans la page
    let mdGrill = annees.length > 1 ? mdGrandEcran : mdPetitEcran;
    // Création du graphique
    let graphic =
        annees.length > 1 ? (
            <Grid item xs={12} md={6}>
                {loading ? (
                    <Skeleton variant="rectangular" height={250}></Skeleton>
                ) : (
                    <Fragment>
                        <Typography variant={'body1'} sx={{ textAlign: 'center' }}>
                            Consommations annuelles
                        </Typography>
                        <BarChart
                            className={'chart'}
                            xAxis={[
                                {
                                    id: 'annee',
                                    data: annees,
                                    scaleType: 'band',
                                },
                            ]}
                            series={[
                                {
                                    data: quantites,
                                },
                            ]}
                            width={500}
                            height={300}
                        />
                    </Fragment>
                )}
            </Grid>
        ) : (
            <Fragment></Fragment>
        );

    let containerConsommations =
        rows.length > 0 ? (
            <Grid container className={'containerConsommations'} spacing={2} sx={{ marginTop: '5px' }}>
                {graphic}
                <Grid item xs={12} md={mdGrill}>
                    <CustomTable
                        rows={rows}
                        isSearching={isSearching}
                        headCells={headCells}
                        handleOnPageChange={handleOnPageChange}
                        page={page}
                        rowsPerPage={rowsPerPage ? rowsPerPage : 0}
                        totalItems={releves?.totalItems}
                    />
                </Grid>
            </Grid>
        ) : (
            <Box sx={{ marginTop: '30px' }}>
                <Typography sx={{ textAlign: 'center' }} variant={'body1'}>
                    Ancun relevé n'a encore été saisi
                </Typography>
            </Box>
        );

    // Vue à afficher
    const viewConso = configWaterMeter?.compteurTelereleve ? (
        <ConsommationTelereleve contractId={props.contract?.['@id']} config={configWaterMeter} />
    ) : (
        containerConsommations
    );

    return (
        <Paper className="container-conso">
            {props.contract?.informations?.abonne && (
                <>
                    <Typography variant={'h5'}>
                        Contrat : {props.contract.informations.abonne.typeContrat} -{' '}
                        {props.contract.informations.abonne.pdiReference}
                    </Typography>
                    {props.contract.adresseCompteur && (
                        <Typography variant={'body1'}>
                            Lieu de consommation :{' '}
                            {props.contract.adresseCompteur?.adresse_complete ||
                                `${props.contract.adresseCompteur?.numero || ''} ${
                                    props.contract.adresseCompteur?.rue || ''
                                }, ${props.contract.adresseCompteur?.code_postal || ''} ${
                                    props.contract.adresseCompteur?.ville || ''
                                }`.trim()}
                        </Typography>
                    )}
                </>
            )}
            {loading ? <Skeleton variant="rectangular" height={250}></Skeleton> : viewConso}
        </Paper>
    );
}
